<script lang="ts">
    import { _ } from 'svelte-i18n';
    import TemplateSettingsGeneral from '../Settings/TemplateSettingsGeneral.svelte';
    import TelematicsSliderSingle from '../TelematicsSlider/TelematicsSliderSingle.svelte';
    import Select from '../../shared/Select.svelte';
    import { WarehouseApi } from '../Warehouses/WarehouseApi';
    import { get } from 'svelte/store';
    import { api } from '../../../stores/api';
    import { onMount } from 'svelte';
    import ButtonMain from '../../shared/ButtonMain.svelte';
    import { closeModal } from 'svelte-modals';
    import { VehiclesApi } from './VehiclesApi';
    import { vehicleDefaults, type VehicleResponseType } from './types';
    import { vehiclesManager } from './VehiclesApp/vehiclesManager';
    import ProgressBar from '../../shared/ProgressBar.svelte';
    import ButtonSmallBlue from '../../shared/ButtonSmallBlue.svelte';
    import LinkSmallBlack from '../../shared/LinkSmallBlack.svelte';

    export let isOpen: boolean = false;
    export let onCreate: () => void = () => {};

    let vehicleQuantityToCreate: number = 5;
    let warehouseId: string = '1'; // Default warehouse ID
    let warehouses: { id: string; label: string }[] = [];
    let vehiclesProcessed: number = 0;
    let processing: boolean = false;
    let processingJustFinished: boolean = false;

    const MAX_VEHICLES_TO_CREATE = 100; // Maximum number of vehicles to create

    onMount(() => {
        WarehouseApi.getAll(get(api))
            .then(ww => {
                if (ww.length > 0) {
                    warehouseId = ww[0].id.toString(); // Set default to the first warehouse
                    ww.forEach(w => {
                        warehouses.push({ id: w.id.toString(), label: w.name });
                    });
                }
            })
            .catch(error => {
                console.error('Error fetching warehouses:', error);
            });
    });

    function createVehicles() {
        processing = true;
        processingJustFinished = false;
        vehiclesProcessed = 0;
        for (let i = 0; i < vehicleQuantityToCreate; i++) {
            let uniqueId = Math.floor(Math.random() * 1000000);
            let vehicle: VehicleResponseType = {
                ...JSON.parse(JSON.stringify(vehicleDefaults)),
                name: `${i + 1}`,
                warehouse_id: parseInt(warehouseId),
                email: 'created-' + uniqueId.toString() + '@automapa.pl',
            };
            VehiclesApi.createOne(get(api), vehicle)
                .then(response => {
                    console.log('Vehicle created:', response);
                    $vehiclesManager.reset();
                })
                .catch(error => {
                    console.error('Error creating vehicle:', error);
                })
                .finally(() => {
                    vehiclesProcessed++;
                    if (vehiclesProcessed >= vehicleQuantityToCreate) {
                        processing = false;
                        processingJustFinished = true;
                        VehiclesApi.colorize(get(api))
                            .then(() => {
                                $vehiclesManager.reset();
                            })
                            .catch(error => {
                                console.error('Error colorizing vehicles:', error);
                            })
                            .finally(() => {
                                onCreate();
                            });
                    }
                });
        }
    }

    function onClick(evt: any) {
        closeModal();
    }
</script>

{#if isOpen}
    <div role="dialog" class="modal">
        <div class="contents">
            {#if processing && vehicleQuantityToCreate > 0}
                <div
                    style="display: flex; flex-direction: row;  align-items: center;justify-content: center; height: 300px;"
                >
                    <div
                        style="display: flex; justify-content: center; align-items: center; max-height: 100px; width: 400px; margin-bottom: 20px;"
                    >
                        <ProgressBar
                            current={(vehiclesProcessed / vehicleQuantityToCreate) * 100}
                            label={$_('creating_vehicles_progress', { values: { processed: vehiclesProcessed, total: vehicleQuantityToCreate } })}
                        />
                    </div>
                </div>
            {:else if processingJustFinished}
                <div
                    style="display: flex; padding: 50px; flex-direction: column; gap: 10px; font-size: 16px;"
                >
                    <div>
                        {$_('vehicles_created_label')} <span style="font-weight: 600;">{vehiclesProcessed} {$_('vehicles_genitive')}</span>.
                    </div>
                    <div>{$_('create_more_vehicles_question')}</div>
                    <div style="display: flex; flex-direction: column;gap: 10px; margin-top: 20px;">
                        <LinkSmallBlack
                            text={$_('yes_create_more_vehicles')}
                            on:click={() => {
                                processingJustFinished = false;
                            }}
                        />
                        <LinkSmallBlack
                            text={$_('no_enough')}
                            on:click={() => {
                                closeModal();
                            }}
                        />
                    </div>
                </div>
            {:else}
                <div>
                    <TemplateSettingsGeneral>
                        <div slot="label">{$_('set_vehicle_count')}</div>
                        <div slot="content">
                            <TelematicsSliderSingle
                                props={{
                                    min: 1,
                                    max: MAX_VEHICLES_TO_CREATE,
                                    currentValue: vehicleQuantityToCreate,
                                    unit: '',
                                    dangerZoneBelowValue: false,
                                    dangerZoneAboveValue: false,
                                    roundValues: true,
                                    keepInitialValueInSliderRange: true,
                                    inputsAboveSlider: true,
                                    sliderWidth: 400,
                                }}
                                onChange={value => {
                                    vehicleQuantityToCreate = value;
                                }}
                            />
                        </div>
                    </TemplateSettingsGeneral>
                    <TemplateSettingsGeneral>
                        <div slot="label">{$_('select_warehouse_for_vehicles')}</div>
                        <div slot="content">
                            <Select
                                items={warehouses}
                                selected={warehouseId}
                                onChange={selected => {
                                    warehouseId = selected;
                                    return true;
                                }}
                            />
                        </div>
                    </TemplateSettingsGeneral>
                </div>
                <div style="display: flex; justify-content: center;">
                    <div style="display: flex; justify-content: center; max-width: 300px;">
                        <ButtonMain
                            text={$_('create_vehicles')}
                            on:click={() => {
                                createVehicles();
                            }}
                        />
                    </div>
                </div>
            {/if}
        </div>
    </div>
{/if}

<style>
    .modal {
        position: fixed;
        top: 0;
        bottom: 0;
        right: 0;
        left: 0;
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 1002;

        /* allow click-through to backdrop */
        pointer-events: none;
    }

    .contents {
        width: 850px;
        border-radius: 6px;
        background: white;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        pointer-events: auto;
        font-size: 14px;
        padding: 20px 20px 30px 20px;
        min-height: 360px;
        row-gap: 20px;
        /*background: silver;*/
    }

    .scrollable-list {
        overflow-y: auto;
        overflow-x: hidden;
        max-height: 450px;
        background-color: white;
        padding: 20px;
        border-radius: 3px;
        flex: 1 1 auto;
        /*overflow: hidden;*/
    }
    .actions {
        padding: 0 20px 20px 20px;
    }
</style>
